import { useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  CopyIcon,
  GearIcon,
  TranslateIcon,
  UserIcon,
  WalletIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { useAuth } from '../auth';
import {
  SUPPORTED_LANGS,
  getStoredLang,
  setStoredLang,
  useLang,
  useT,
  type Lang,
} from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

const LANG_LABEL: Record<Lang, string> = {
  en: 'English',
  'pt-BR': 'Português (Brasil)',
  es: 'Español',
  zh: '中文',
};

const LANG_FLAG: Record<Lang, string> = {
  en: '🇺🇸',
  'pt-BR': '🇧🇷',
  es: '🇪🇸',
  zh: '🇨🇳',
};

const APP_VERSION =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_APP_VERSION) ||
  '0.0.0-dev';

function short(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

export default function Settings() {
  const t = useT();
  const auth = useAuth();
  const activeLang = useLang();
  useTelegramBackButton();
  useNavStackEntry({ label: t('settings.breadcrumb') });

  const [savedFlash, setSavedFlash] = useState(false);
  const stored = getStoredLang();

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <GearIcon weight="fill" className="size-3" />
        {t('settings.eyebrow')}
      </span>
    ),
    [t],
  );

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('settings.title')}
        description={t('settings.description')}
        shortDescription={t('settings.title')}
      />
    );
  }

  const onPickLang = (lang: Lang | null) => {
    setStoredLang(lang);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1800);
  };

  const onCopy = (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(text);
    }
  };

  return (
    <ScreenShell
      eyebrow={eyebrow}
      title={t('settings.title')}
      description={t('settings.description')}
    >
      <NavBar />

      {auth.state === 'error' && (
        <Alert tone="danger">
          <AlertTitle>{t('common.auth_error', { error: '' })}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardEyebrow>
            <TranslateIcon weight="fill" className="size-3" />
            {t('settings.lang_title')}
          </CardEyebrow>
          <p className="text-muted-foreground text-[13px] leading-relaxed">
            {t('settings.lang_desc')}
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2">
            <LangRow
              label={t('settings.lang_auto')}
              flag="✨"
              active={stored === null}
              onClick={() => onPickLang(null)}
              sublabel={`→ ${LANG_LABEL[activeLang]}`}
            />
            {SUPPORTED_LANGS.map((lang) => (
              <LangRow
                key={lang}
                label={LANG_LABEL[lang]}
                flag={LANG_FLAG[lang]}
                active={stored === lang}
                onClick={() => onPickLang(lang)}
              />
            ))}
          </div>
          {savedFlash && (
            <p className="text-success-foreground mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold">
              <CheckCircleIcon weight="fill" className="size-3.5 text-emerald-500" />
              {t('settings.lang_saved')}
            </p>
          )}
        </CardContent>
      </Card>

      {auth.state === 'ready' && (
        <Card>
          <CardHeader>
            <CardEyebrow>
              <WalletIcon weight="fill" className="size-3" />
              {t('settings.address_title')}
            </CardEyebrow>
            <CardTitle className="font-mono text-base tabular-nums">
              {short(auth.address)}
            </CardTitle>
            <p className="text-muted-foreground text-[12px] leading-relaxed">
              {t('settings.address_desc')}
            </p>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCopy(auth.auth.depositWalletAddress ?? auth.address)}
            >
              <CopyIcon weight="bold" className="size-4" />
              {t('common.copy_full_address')}
            </Button>
          </CardContent>
        </Card>
      )}

      {auth.state === 'ready' && (
        <Card>
          <CardHeader>
            <CardEyebrow>
              <UserIcon weight="fill" className="size-3" />
              {t('settings.session_title')}
            </CardEyebrow>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-[13px]">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">
                {t('settings.signed_in_as')}
              </span>
              <span className="text-foreground font-mono tabular-nums">
                {auth.auth.externalUserId}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">
                {t('settings.user_id_label')}
              </span>
              <span className="text-foreground font-mono tabular-nums">
                {auth.auth.telegramUserId || '—'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardEyebrow>
            <GearIcon weight="fill" className="size-3" />
            {t('settings.about_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-3 text-[13px]">
          <span className="text-muted-foreground">
            {t('settings.version_label')}
          </span>
          <Badge tone="neutral" className="font-mono">{APP_VERSION}</Badge>
        </CardContent>
      </Card>
    </ScreenShell>
  );
}

function LangRow({
  label,
  sublabel,
  flag,
  active,
  onClick,
}: {
  label: string;
  sublabel?: string;
  flag: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 text-left ring-1 transition-all',
        active
          ? 'bg-primary/10 ring-primary/30'
          : 'bg-card/85 ring-foreground/10 hover:bg-card hover:ring-foreground/25',
      )}
    >
      <span className="flex items-center gap-2.5">
        <span aria-hidden className="text-base leading-none">{flag}</span>
        <span className="flex flex-col">
          <span className="text-foreground text-[14px] font-semibold">
            {label}
          </span>
          {sublabel && (
            <span className="text-muted-foreground text-[11px]">{sublabel}</span>
          )}
        </span>
      </span>
      {active && (
        <CheckCircleIcon weight="fill" className="size-5 text-emerald-500" />
      )}
    </button>
  );
}
